import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { DateRange } from 'react-day-picker';
import { startOfMonth, format } from 'date-fns';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  ConsolidatedMetricsList,
  AddAccountControl,
  useConsolidatedMetrics,
} from '@/modules/analytics-v2/consolidated';
import {
  readSystemAdminUIConfig,
  patchSystemAdminUIConfig,
  type SelectedAccountRef,
} from '@/modules/analytics-v2/system-admin-ui-config';
import { useAnalyticsSelection } from '@/modules/analytics-v2/use-analytics-selection';

// Keep in sync with the hook's MAX_CONCURRENCY.
const MAX_ACCOUNTS = 10;

// Always opens on the current month (not the last saved range, which went stale).
const currentMonth = (): DateRange => ({ from: startOfMonth(new Date()), to: new Date() });

// Super-admin gated; the backend only honors the per-account override for super admins.
export default function InternalAnalyticsDashboard() {
  const [accounts, setAccounts] = useState<SelectedAccountRef[]>(
    () => readSystemAdminUIConfig().consolidatedAccounts ?? [],
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>(currentMonth);
  const navigate = useNavigate();
  const [, setSelection] = useAnalyticsSelection();

  // Persist so a refresh rebuilds the same table.
  useEffect(() => {
    patchSystemAdminUIConfig({ consolidatedAccounts: accounts });
  }, [accounts]);

  const fromDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '';
  const toDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '';

  const accountIds = useMemo(
    () => accounts.map((a) => Number(a.id)).filter((n) => Number.isInteger(n) && n > 0),
    [accounts],
  );

  const { rows, retry } = useConsolidatedMetrics(accountIds, fromDate, toDate);

  const addAccount = (account: SelectedAccountRef) => {
    setAccounts((prev) => {
      if (prev.some((a) => a.id === account.id) || prev.length >= MAX_ACCOUNTS) return prev;
      return [...prev, account];
    });
  };

  const removeAccount = (id: string) => {
    setAccounts((prev) => prev.filter((a) => a.id !== id));
  };

  // An account opens its own pages, on the range this list shows.
  const openAccount = (account: SelectedAccountRef) => {
    if (!fromDate || !toDate) return;
    setSelection({ account, buId: null, startDate: fromDate, endDate: toDate });
    navigate('/analytics/internal/message-metrics');
  };

  return (
    <div className='flex flex-col gap-6 p-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-base font-semibold'>Overview</h1>
          <p className='text-xs text-muted-foreground'>Consolidated AI performance across accounts</p>
        </div>
        <div className='flex items-center gap-3'>
          <AddAccountControl
            selectedIds={accounts.map((a) => a.id)}
            onAdd={addAccount}
            atCapacity={accounts.length >= MAX_ACCOUNTS}
          />
          <DateRangePicker
            value={{ startDate: dateRange?.from, endDate: dateRange?.to }}
            onChange={(range) => setDateRange({ from: range.startDate, to: range.endDate })}
          />
        </div>
      </div>

      {accounts.length === 0 ? (
        <div className='flex h-64 flex-col items-center justify-center gap-1 rounded-3xl border border-dashed border-gray-300 text-sm text-muted-foreground'>
          <p>No accounts added yet.</p>
          <p className='text-xs'>Use "Add account" to build a consolidated view.</p>
        </div>
      ) : (
        <ConsolidatedMetricsList
          accounts={accounts}
          rows={rows}
          onOpen={openAccount}
          onRemove={removeAccount}
          onRetry={retry}
        />
      )}
    </div>
  );
}
